/**
 * Le dépôt de fichiers, côté navigateur (docs/09 §2).
 *
 * Trois appels, dont **un qui ne passe pas par notre API** :
 *
 *   1. `POST /files/upload-intent` → on obtient une URL présignée
 *   2. `PUT <upload_url>`          → directement chez le fournisseur
 *   3. `POST /files/{id}/confirm`  → le serveur vérifie ce qui est arrivé
 *
 * L'étape 2 utilise `fetch` **nu**, et c'est la seule exception à la règle
 * « aucun `fetch` direct hors de `shared/api/client.ts` ». La raison n'est pas
 * un oubli : le client de l'application ajoute un `Authorization: Bearer …` à
 * tout ce qu'il envoie, et cet en-tête ne fait pas partie de ce que la signature
 * S3 couvre. Le fournisseur le verrait, le trouverait non signé, et refuserait
 * la requête avec un message parlant de credentials — qui ne désigne pas sa
 * cause. Envoyer notre jeton de session à un tiers serait par ailleurs une fuite
 * en soi.
 *
 * ⚠️ **Rien ici ne protège quoi que ce soit.** Les gardes — permission,
 * entitlement, quota, type MIME, taille — sont toutes côté serveur et toutes
 * revérifiées. Ce que ce module apporte, c'est d'éviter à l'utilisateur de
 * découvrir un refus après avoir attendu la fin d'un transfert de 40 Mo.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { sendToObjectStore } from "@/features/storage/api/upload-transport";
import { api, expectNoContent, unwrap } from "@/shared/api/client";
import { API_BASE_URL } from "@/shared/api/config";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { billingKeys, storageKeys } from "@/shared/api/query-keys";

export type StoredFile = components["schemas"]["FileResponse"];
export type UploadIntent = components["schemas"]["UploadIntentResponse"];
export type UploadPolicy = components["schemas"]["UploadPolicyResponse"];
export type FilePurpose = components["schemas"]["FilePurpose"];

export interface UploadInput {
  file: File;
  purpose: FilePurpose;
}

/**
 * Les types acceptés pour un usage, **dits par le serveur**.
 *
 * L'attribut `accept` du sélecteur en sort. Recopier l'allowlist en TypeScript
 * la ferait diverger au premier type ajouté, et proposerait à l'utilisateur un
 * format que l'API refuse ensuite — ce qu'il ne découvrirait qu'après avoir
 * choisi son fichier.
 */
export function useUploadPolicy(purpose: FilePurpose): UseQueryResult<UploadPolicy, ApiError> {
  return useQuery({
    queryKey: storageKeys.policy(purpose),
    queryFn: () => unwrap(api.GET("/api/v1/files/policy", { params: { query: { purpose } } })),
    // La réponse est une constante du produit : elle ne dépend ni de
    // l'utilisateur ni de l'instant. La recharger à chaque montage d'un
    // formulaire serait une requête pour une valeur qui ne bouge qu'au
    // déploiement.
    staleTime: Infinity,
  });
}

/**
 * Le flux complet, en une mutation.
 *
 * Les trois étapes sont enchaînées ici plutôt qu'exposées séparément : entre
 * l'intention et la confirmation, une ligne `pending` existe en base, et un
 * appelant qui oublierait la troisième laisserait un fichier que rien ne sert
 * et que seul le balayage horaire finirait par retirer. Une seule mutation rend
 * l'oubli impossible.
 *
 * Invalide les droits **et** la consommation : un dépôt change le nombre de
 * mébioctets occupés, que `GET /billing/usage` sert à l'écran de l'offre.
 */
export function useUploadFile() {
  const queryClient = useQueryClient();
  return useMutation<StoredFile, ApiError, UploadInput>({
    mutationFn: async ({ file, purpose }) => {
      const intent = await unwrap(
        api.POST("/api/v1/files/upload-intent", {
          body: {
            purpose,
            filename: file.name,
            content_type: file.type,
            size_bytes: file.size,
          },
        }),
      );

      await sendToObjectStore(intent, file);

      return unwrap(
        api.POST("/api/v1/files/{file_id}/confirm", {
          params: { path: { file_id: intent.file_id } },
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.all });
      void queryClient.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

export function useMyFiles(purpose?: FilePurpose): UseQueryResult<StoredFile[], ApiError> {
  return useQuery({
    queryKey: storageKeys.list(purpose),
    queryFn: async () =>
      (
        await unwrap(
          api.GET("/api/v1/files", {
            params: { query: purpose === undefined ? {} : { purpose } },
          }),
        )
      ).items,
  });
}

/**
 * Supprime un fichier.
 *
 * Peut échouer en **409** si le fichier sert encore : l'erreur porte la liste
 * des rattachements, et c'est elle que l'écran doit montrer. Un message
 * générique obligerait l'enseignant à rouvrir ses cours un par un pour trouver
 * lequel retient l'image.
 */
export function useDeleteFile(): UseMutationResult<void, ApiError, { fileId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId }: { fileId: string }) =>
      expectNoContent(
        api.DELETE("/api/v1/files/{file_id}", { params: { path: { file_id: fileId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.all });
      void queryClient.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

/**
 * L'adresse de téléchargement d'un fichier.
 *
 * Une URL **de notre API**, pas celle du fournisseur : la route répond 302 vers
 * une URL présignée après avoir vérifié l'accès. Construire l'URL du magasin
 * ici court-circuiterait cette garde — et pour un fichier privé, l'URL ne serait
 * de toute façon pas signée.
 */
export function downloadUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/files/${fileId}/download`;
}
